import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '@/utils/axios';
import { toast } from 'sonner';
import { clearCart } from './cartSlice';

// Initial state
const initialState = {
  user: null,
  loading: false,
  error: null,
  userDetails: null,
  userDetailsLoading: false,
  addresses: [],
  addressesLoading: false,
  transactions: [],
  transactionsLoading: false,
};

// Async thunks
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/auth/check');
      return res.data;
    } catch (error) {
      // Don't reject for 401/403 - just return null user
      if (error.response?.status === 401 || error.response?.status === 403) {
        return null;
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to check auth');
    }
  }
);

export const getUserDetails = createAsyncThunk(
  'auth/getUserDetails',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/auth/details');
      return res.data;
    } catch (error) {
      console.error('Failed to fetch user details', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user details');
    }
  }
);

export const updateUserDetails = createAsyncThunk(
  'auth/updateUserDetails',
  async (data, { rejectWithValue, getState }) => {
    try {
      const res = await axios.put('/auth/update', data);
      // Update user in state if it exists
      const state = getState();
      if (state.auth.user) {
        return { ...state.auth.user, ...res.data };
      }
      return res.data;
    } catch (error) {
      console.error('Failed to update user details', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to update user details');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword }, { rejectWithValue }) => {
    try {
      const res = await axios.post('/auth/change-password', {
        oldPassword,
        newPassword,
      });
      return res.data;
    } catch (error) {
      console.error('Failed to change password', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to change password');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get user before logout to clear database cart
      const state = getState();
      const user = state.auth?.user;
      
      await axios.post('/auth/logout');
      // Clear cart on logout (both localStorage and database if user was logged in)
      dispatch(clearCart(user));
      return null;
    } catch (err) {
      console.error('Logout failed:', err);
      // Even if logout fails, clear local state and database cart
      const state = getState();
      const user = state.auth?.user;
      dispatch(clearCart(user));
      return null;
    }
  }
);

export const checkEmailVerificationStatus = createAsyncThunk(
  'auth/checkEmailVerificationStatus',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post('/auth/check-email');
      return res.data; // { email, isVerified }
    } catch (error) {
      console.error('Failed to check verification status', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to check verification status');
    }
  }
);

export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post('/auth/resend-verification');
      return res.data; // { message: '...' }
    } catch (error) {
      console.error('Failed to resend verification email', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to resend verification email');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get user before deletion to clear database cart
      const state = getState();
      const user = state.auth?.user;
      
      await axios.delete('/auth/delete');
      toast.success('Account deleted successfully.');
      // Clear cart on account deletion (both localStorage and database)
      dispatch(clearCart(user));
      return null;
    } catch (error) {
      console.error('Failed to delete account', error);
      toast.error('Failed to delete account. Please try again later.');
      return rejectWithValue(error.response?.data?.message || 'Failed to delete account');
    }
  }
);

// Addresses
export const getAddresses = createAsyncThunk(
  'auth/getAddresses',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/shipping');
      return res.data; // returns an array of address objects
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
      toast.error('Unable to load addresses.');
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch addresses');
    }
  }
);

export const addAddress = createAsyncThunk(
  'auth/addAddress',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post('/shipping/add', data);
      toast.success('Address added successfully.');
      return res.data; // includes addressId
    } catch (error) {
      console.error('Failed to add address:', error);

      // Handle validation errors specifically
      if (error.response?.status === 400 && error.response?.data?.error?.details) {
        const validationErrors = error.response.data.error.details;
        const errorMessages = validationErrors.map(err => `${err.field}: ${err.message}`).join('\n');
        toast.error(`Validation failed:\n${errorMessages}`);
        return rejectWithValue(errorMessages);
      }

      // Generic error handling
      const errorMessage = error.response?.data?.message || error.response?.data?.error?.message || 'Failed to add address';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAddress = createAsyncThunk(
  'auth/updateAddress',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`/shipping/update/${id}`, data);
      toast.success('Address updated successfully.');
      return res.data;
    } catch (error) {
      console.error('Failed to update address:', error);

      // Handle validation errors specifically
      if (error.response?.status === 400 && error.response?.data?.error?.details) {
        const validationErrors = error.response.data.error.details;
        const errorMessages = validationErrors.map(err => `${err.field}: ${err.message}`).join('\n');
        toast.error(`Validation failed:\n${errorMessages}`);
        return rejectWithValue(errorMessages);
      }

      // Generic error handling
      const errorMessage = error.response?.data?.message || error.response?.data?.error?.message || 'Failed to update address';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAddress = createAsyncThunk(
  'auth/deleteAddress',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/shipping/delete/${id}`);
      toast.success('Address deleted successfully.');
      return id;
    } catch (error) {
      console.error('Failed to delete address:', error);
      toast.error('Failed to delete address. Please try again.');
      return rejectWithValue(error.response?.data?.message || 'Failed to delete address');
    }
  }
);

// Transactions
export const getTransactions = createAsyncThunk(
  'auth/getTransactions',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/transactions');
      return res.data.data || [];
    } catch (error) {
      console.error('Failed to fetch transactions', error);
      toast.error('Failed to load transactions.');
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const downloadTransactionReport = createAsyncThunk(
  'auth/downloadTransactionReport',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/transactions/pdf', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transactions.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download the transaction report.');
      return rejectWithValue(error.response?.data?.message || 'Failed to download report');
    }
  }
);

// Newsletter
export const subscribeToNewsletter = createAsyncThunk(
  'auth/subscribeToNewsletter',
  async ({ email, name = '' }, { rejectWithValue }) => {
    try {
      const res = await axios.post('/subscribe', { email, name });
      toast.success('Subscribed successfully!');
      return res.data;
    } catch (error) {
      console.error('Subscription failed:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0]?.msg ||
        'Subscription failed. Please try again.';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.userDetails = null;
      state.addresses = [];
      state.transactions = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Check auth
    builder
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.error = action.payload;
      });

    // Get user details
    builder
      .addCase(getUserDetails.pending, (state) => {
        state.userDetailsLoading = true;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.userDetailsLoading = false;
        state.userDetails = action.payload;
      })
      .addCase(getUserDetails.rejected, (state) => {
        state.userDetailsLoading = false;
        state.userDetails = null;
      });

    // Update user details
    builder
      .addCase(updateUserDetails.fulfilled, (state, action) => {
        state.user = action.payload;
        state.userDetails = action.payload;
      });

    // Change password
    builder
      .addCase(changePassword.fulfilled, (state) => {
        // Password changed successfully
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.userDetails = null;
        state.addresses = [];
        state.transactions = [];
      });

    // Delete account
    builder
      .addCase(deleteAccount.fulfilled, (state) => {
        state.user = null;
        state.userDetails = null;
        state.addresses = [];
        state.transactions = [];
      });

    // Get addresses
    builder
      .addCase(getAddresses.pending, (state) => {
        state.addressesLoading = true;
      })
      .addCase(getAddresses.fulfilled, (state, action) => {
        state.addressesLoading = false;
        state.addresses = action.payload;
      })
      .addCase(getAddresses.rejected, (state) => {
        state.addressesLoading = false;
        state.addresses = [];
      });

    // Add address
    builder
      .addCase(addAddress.fulfilled, (state, action) => {
        state.addresses.push(action.payload);
      });

    // Update address
    builder
      .addCase(updateAddress.fulfilled, (state, action) => {
        const index = state.addresses.findIndex((addr) => addr.id === action.payload.id);
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
      });

    // Delete address
    builder
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter((addr) => addr.id !== action.payload);
      });

    // Get transactions
    builder
      .addCase(getTransactions.pending, (state) => {
        state.transactionsLoading = true;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        state.transactions = action.payload;
      })
      .addCase(getTransactions.rejected, (state) => {
        state.transactionsLoading = false;
        state.transactions = [];
      });
  },
});

export const { setUser, clearUser, clearError } = authSlice.actions;

// Selectors
export const selectUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserDetails = (state) => state.auth.userDetails;
export const selectUserDetailsLoading = (state) => state.auth.userDetailsLoading;
export const selectAddresses = (state) => state.auth.addresses;
export const selectAddressesLoading = (state) => state.auth.addressesLoading;
export const selectTransactions = (state) => state.auth.transactions;
export const selectTransactionsLoading = (state) => state.auth.transactionsLoading;

export default authSlice.reducer;

